import { Component, Inject, OnInit, PLATFORM_ID, Renderer2, ViewChild } from '@angular/core';
import { Router, NavigationStart, NavigationCancel, NavigationEnd } from '@angular/router';
import { isPlatformBrowser, Location, LocationStrategy, PathLocationStrategy } from '@angular/common';
import { filter } from 'rxjs/operators'
import { NavbarComponent } from './components/common/navbar/navbar.component';
import { AuthService } from './services/auth/auth.service';
import { ConsentService, ConsentSource } from './services/consent/consent.service';

declare let $: any;

const SESSION_EXCLUDED = ['/acceder', '/registrarse', '/cambiar-password', '/verificar', '/oauth'];
const PROTECTED_ROUTES = [
  '/empresa', '/planes', '/postulados', '/vacante/nuevo', '/vacante/editar',
  '/mis-cvs', '/candidatos', '/acerca-de-nosotros', '/editar-perfil',
  '/mis-suscripciones', '/alertas', '/perfil', '/mis-postulaciones',
  '/admin',
];

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    providers: [
        Location, {
            provide: LocationStrategy,
            useClass: PathLocationStrategy
        }
    ]
})
export class AppComponent implements OnInit {
    location: any;
    routerSubscription: any;
    isAdminRoute = false;

    // Modal de consent (aviso de privacidad + términos)
    consentModalOpen = false;
    consentModalSource: ConsentSource = 'primer_login';

    @ViewChild('menuBar') menuBar: NavbarComponent;

    constructor(
        private router: Router,
        @Inject(PLATFORM_ID) private platformId: object,
        private renderer: Renderer2,
        private authService: AuthService,
        private consent: ConsentService
    ) {}

    ngOnInit(){
        this.isAdminRoute = window.location.pathname.startsWith('/admin');

        // Suscripción a solicitudes de modal de consent (desde interceptor o componentes).
        this.consent.modalRequests().subscribe(source => {
            this.consentModalSource = source;
            this.consentModalOpen = true;
        });

        // Si hay sesión NO admin, sincroniza el estado de consent.
        // Admin no participa del flujo de privacidad (no es candidato/empresa);
        // su backend devuelve 404 en /privado/me/consent/status.
        if (typeof localStorage !== 'undefined') {
            const raw = localStorage.getItem('currentUser');
            if (raw) {
                let role: string | undefined;
                try { role = JSON.parse(raw)?.role; } catch {}
                if (role && role !== 'admin') {
                    this.consent.syncFromServer().subscribe();
                }
            }
        }

        // Verificar expiración al arrancar la app
        this.checkSession();

        this.recallJsFuntions();
        this.router.events.subscribe(event => {
            if (event instanceof NavigationEnd) {
              const currentUrl = this.router.url;
              this.isAdminRoute = currentUrl.startsWith('/admin');

              // Verificar expiración en cada navegación
              this.checkSession(currentUrl);

              try {
                const whatsappButton = this.renderer.selectRootElement('#whatsapp-button', true);
                if (currentUrl.includes('/acceder') || currentUrl.includes('/registrarse')
                  || currentUrl.includes('/cambiar-password') || this.isAdminRoute) {
                  this.renderer.setStyle(whatsappButton, 'display', 'none');
                } else {
                  this.renderer.setStyle(whatsappButton, 'display', 'flex');
                }
              } catch (_) {}
            }
          });
    }

    private checkSession(currentUrl?: string): void {
        const url = currentUrl || window.location.pathname;
        const isPublic = SESSION_EXCLUDED.some(p => url.includes(p));
        if (isPublic) return;

        // Usuario anónimo (incógnito / sin sesión): puede navegar libremente
        if (!localStorage.getItem('currentUser')) return;

        if (this.authService.isTokenExpired()) {
            this.authService.cerrarSesion();
            // Solo redirigir a /acceder si la ruta requiere autenticación.
            // En rutas públicas (home, vacantes, etc.) solo se limpia la sesión.
            if (PROTECTED_ROUTES.some(p => url.startsWith(p))) {
                this.router.navigate(['/acceder']);
            }
        }
    }

    recallJsFuntions() {
        this.router.events
        .subscribe((event) => {
            if ( event instanceof NavigationStart ) {
                try { if (typeof $ !== 'undefined') $('.loader').fadeIn('slow'); } catch(e) {}
            }
        });
        this.routerSubscription = this.router.events
        .pipe(filter(event => event instanceof NavigationEnd || event instanceof NavigationCancel))
        .subscribe(event => {

            if (isPlatformBrowser(this.platformId)) {
                try { if (typeof $ !== 'undefined') { $('.loader').fadeOut('slow'); } } catch(e) {}
            }

            this.location = this.router.url;

            if (!this.location.match("/candidatos")) {
                if (localStorage.getItem("salario")) localStorage.removeItem("salario");
                if (localStorage.getItem("idEstado")) localStorage.removeItem("idEstado");
            }

            if(!this.location.match('/vacante/editar') && !this.location.match('/vacantes/detalle') && !this.location.match('/vacantes')) {
                if (localStorage.getItem("idVacante")) localStorage.removeItem("idVacante");
            }

            if(!this.location.match('/vacante')){
                if (localStorage.getItem("idEmpresa")) localStorage.removeItem("idEmpresa");
            }

            if (!(event instanceof NavigationEnd)) return;

            if (isPlatformBrowser(this.platformId)) {
                window.scrollTo(0, 0);
            }
        });
    }

    actualizarMenuBar(): void{
        this.menuBar?.ngOnInit();
    }

    onConsentAccepted(): void {
        this.consentModalOpen = false;
        this.consent.clearModalRequest();
    }

    onConsentCancelled(): void {
        this.consentModalOpen = false;
        this.consent.clearModalRequest();
    }
}
